'use client';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { ProductCard } from './product-card';
/**
 * Horizontally-scrolling row of product cards rendered inline in the
 * chat transcript. Native scroll-snap on touch; on desktop the scroll
 * arrows fade in only when the row is actually overflowing AND the
 * pointer is over the carousel — keeping the chrome quiet by default.
 */
export function ProductCarousel({ products, onAddToCart, className }) {
    const scrollerRef = React.useRef(null);
    const [atStart, setAtStart] = React.useState(true);
    const [atEnd, setAtEnd] = React.useState(false);
    const [overflows, setOverflows] = React.useState(false);
    React.useEffect(() => {
        const el = scrollerRef.current;
        if (!el)
            return;
        const update = () => {
            const left = el.scrollLeft;
            const max = el.scrollWidth - el.clientWidth;
            setAtStart(left <= 1);
            setAtEnd(left >= max - 1);
            setOverflows(max > 1);
        };
        update();
        el.addEventListener('scroll', update, { passive: true });
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => {
            el.removeEventListener('scroll', update);
            ro.disconnect();
        };
    }, [products.length]);
    const scroll = (direction) => {
        const el = scrollerRef.current;
        if (!el)
            return;
        const distance = Math.max(el.clientWidth - 60, 200);
        el.scrollBy({
            left: direction === 'next' ? distance : -distance,
            behavior: 'smooth',
        });
    };
    if (!products.length)
        return null;
    return (_jsxs("div", { className: cn('group relative -mx-4 mt-1 mb-1', className), children: [_jsx("div", { ref: scrollerRef, className: cn('flex snap-x snap-mandatory gap-3 overflow-x-auto scroll-smooth px-4 pt-1 pb-2', 
                // Hide the scrollbar without losing scrollability — works on
                // Webkit and Firefox. The scroll arrows take its place.
                '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden'), children: products.map((p, i) => (_jsx("div", { className: "snap-start", children: _jsx(ProductCard, { product: p, onAddToCart: onAddToCart }) }, p.id || `product-${i}`))) }), overflows && !atStart && (_jsx("div", { className: "pointer-events-none absolute inset-y-0 left-0 w-6 bg-gradient-to-r from-gray-50 to-transparent" })), overflows && !atEnd && (_jsx("div", { className: "pointer-events-none absolute inset-y-0 right-0 w-6 bg-gradient-to-l from-gray-50 to-transparent" })), overflows && (_jsxs(_Fragment, { children: [_jsx("button", { type: "button", "aria-label": "Scroll products left", onClick: () => scroll('prev'), className: cn('absolute top-1/2 left-2 z-10 hidden -translate-y-1/2 items-center justify-center', 'h-8 w-8 rounded-full border border-gray-200 bg-white shadow-md', 'opacity-0 transition-opacity duration-150 group-hover:opacity-100', 'hover:bg-gray-50 disabled:opacity-0 md:flex'), disabled: atStart, children: _jsx(CaretLeftIcon, { size: 14, weight: "bold", className: "text-gray-700" }) }), _jsx("button", { type: "button", "aria-label": "Scroll products right", onClick: () => scroll('next'), className: cn('absolute top-1/2 right-2 z-10 hidden -translate-y-1/2 items-center justify-center', 'h-8 w-8 rounded-full border border-gray-200 bg-white shadow-md', 'opacity-0 transition-opacity duration-150 group-hover:opacity-100', 'hover:bg-gray-50 disabled:opacity-0 md:flex'), disabled: atEnd, children: _jsx(CaretRightIcon, { size: 14, weight: "bold", className: "text-gray-700" }) })] }))] }));
}
